import { Edit } from "@mui/icons-material";
import {
  Button,
  Divider,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
} from "@mui/material";
import {
  Card,
  CardActions,
  ContentWithQuestions,
  queryClient,
  useError,
} from "@ssf/components";
import { type Club, type Member, Sex } from "@ssf/memdb-api";
import {
  contactSchema,
  MemberFormCard,
  memberSchema,
  useUpdateMember,
} from "@ssf/memdb-components";
import {
  Account,
  CakeVariant,
  Email,
  GenderMaleFemale,
  Home,
  Phone,
} from "mdi-material-ui";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import { matchesClubAddress } from "../lib/matchesClubAddress.js";

const schemaWithName = memberSchema.merge(
  // @ts-ignore
  z.object({
    // @ts-ignore
    email: contactSchema.shape.email.optional().or(z.literal("")),
    // @ts-ignore
    phone: contactSchema.shape.phone.optional().or(z.literal("")),
  }),
);

const schemaWithoutName = schemaWithName.omit({
  firstname: true,
  lastname: true,
});

export const MemberCard = ({
  member,
  club,
  disableName,
}: {
  club: Pick<Club, "id" | "street" | "zipcode">;
  member: Pick<
    Member,
    | "id"
    | "firstname"
    | "lastname"
    | "birthday"
    | "sex"
    | "street"
    | "zipcode"
    | "city"
    | "phone"
    | "email"
  >;
  disableName?: boolean;
}) => {
  const { t } = useTranslation("MemberCard");
  const { addError } = useError();
  const [edit, setEdit] = useState(false);

  const formSchema = disableName ? schemaWithoutName : schemaWithName;

  const { isPending, mutate: updateMember } = useUpdateMember({
    onSuccess() {
      setEdit(false);
    },
    onSettled() {
      queryClient.invalidateQueries({ queryKey: ["useMember", member.id] });
    },
  });

  const initialMember = {
    firstname: member.firstname,
    lastname: member.lastname,
    birthday: member.birthday,
    sex: member.sex,
    street: member.street,
    zipcode: member.zipcode,
    city: member.city,
    phone: member.phone,
    email: member.email,
  };

  return (
    <ContentWithQuestions tag="member">
      {edit ? (
        <MemberFormCard
          title={t("title", member)}
          description={t("description")}
          // @ts-ignore
          schema={schemaWithName}
          // @ts-ignore
          formSchema={formSchema}
          // @ts-ignore
          member={initialMember}
          action={t("action")}
          onSubmit={(data) => {
            // MemberFormCard validates values with schemaWithName, but types them as schema fields.
            const values = data as unknown as z.infer<typeof schemaWithName>;
            if (matchesClubAddress(values, club)) {
              addError({
                message: "a member can't have the same address as the club",
              });
              return;
            }
            updateMember({ id: member.id, ...values, clubid: club.id });
          }}
          isLoading={isPending}
        />
      ) : (
        <>
          <Card
            title={`${member.firstname} ${member.lastname}`}
            icon={<Account />}
            disablePadding
          >
            <List sx={{ ".MuiSvgIcon-root": { mr: 1 } }} dense>
              <ListItem>
                <CakeVariant />
                <ListItemText>{member.birthday}</ListItemText>
              </ListItem>
              <ListItem>
                <GenderMaleFemale />
                <ListItemText>
                  {t("sex", { context: Sex[member.sex] })}
                </ListItemText>
              </ListItem>
              <ListItem>
                <Home />
                <ListItemText>{t("address", member)}</ListItemText>
              </ListItem>
              {(member.phone || member.email) && <Divider sx={{ my: 1 }} />}
              {member.phone && (
                <ListItem disablePadding>
                  <ListItemButton component="a" href={`tel:${member.phone}`}>
                    <Phone />
                    <ListItemText>{member.phone}</ListItemText>
                  </ListItemButton>
                </ListItem>
              )}
              {member.email && (
                <ListItem disablePadding>
                  <ListItemButton component="a" href={`mailto:${member.email}`}>
                    <Email />
                    <ListItemText>{member.email}</ListItemText>
                  </ListItemButton>
                </ListItem>
              )}
            </List>
          </Card>
          <CardActions>
            <Button
              variant="contained"
              startIcon={<Edit />}
              onClick={() => setEdit(true)}
            >
              {t("edit")}
            </Button>
          </CardActions>
        </>
      )}
    </ContentWithQuestions>
  );
};
